import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, HeartPulse, ShoppingCart, Factory, Cpu, Radio } from 'lucide-react';
import { industries } from '../mockData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import Contact from '../components/Contact';

const iconMap = {
  'building-2': Building2,
  'heart-pulse': HeartPulse,
  'shopping-cart': ShoppingCart,
  factory: Factory,
  cpu: Cpu,
  radio: Radio
};

const Industries = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              <span className="text-[#1A3E6E]">Industries</span> We Serve
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our expertise spans across multiple industries, delivering AI and software solutions 
              tailored to address unique sector challenges and opportunities.
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry) => {
              const IconComponent = iconMap[industry.icon];
              return (
                <Card
                  key={industry.id}
                  onClick={() => navigate('/contact')}
                  className="hover:shadow-xl transition-all duration-300 border-2 hover:border-[#1A3E6E] group cursor-pointer"
                >
                  <CardHeader>
                    <div className="flex items-center space-x-4">
                      <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-[#1A3E6E] transition-colors duration-300">
                        <IconComponent className="text-[#1A3E6E] group-hover:text-white transition-colors duration-300" size={28} />
                      </div>
                      <CardTitle className="text-xl group-hover:text-[#1A3E6E] transition-colors">
                        {industry.name}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 mb-4">
                      {industry.description}
                    </CardDescription>
                    <p className="text-[#1A3E6E] text-sm font-semibold group-hover:underline">
                      Contact us to learn more →
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industry Solutions Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900">Industry-Specific Solutions</h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              We understand that every industry has unique challenges. Our solutions are tailored to meet your specific needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Deep Industry Knowledge</h3>
              <p className="text-gray-600 leading-relaxed">
                Our team includes specialists with years of experience in your industry. We understand 
                regulatory requirements, market dynamics, and technical challenges specific to your sector.
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Proven Track Record</h3>
              <p className="text-gray-600 leading-relaxed">
                We've delivered successful projects across all major industries, from AI-powered trading 
                systems in finance to predictive maintenance in manufacturing.
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Compliance & Security</h3>
              <p className="text-gray-600 leading-relaxed">
                We ensure all solutions meet industry-specific compliance requirements including HIPAA, 
                PCI-DSS, SOC 2, and other relevant standards.
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Scalable Solutions</h3>
              <p className="text-gray-600 leading-relaxed">
                Our solutions are built to scale with your business, whether you're a startup or an 
                enterprise, ensuring long-term value and growth potential.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <Contact />
    </div>
  );
};

export default Industries;
